import { useSortable } from '@dnd-kit/sortable'
import { MinimumRow, PricingRow } from '../usePricingTable'
import { CSS } from '@dnd-kit/utilities'
import {
  GreyGrey10,
  GreyGrey60,
  GreyWhite,
  IndigoIndigo10,
  Primary12Regular,
  Primary14SemiBold
} from '@sequencehq/core-components/theme'
import { COMPACT_COLUMN_WIDTHS, STYLES } from './pricingTableCompact.constants'
import {
  getPricingModelLabel,
  toBillingFrequencyLabel
} from '@sequencehq/utils'
import {
  Button,
  IconDragHandle,
  SequenceMenu,
  Tooltip,
  IconEllipsisHorizontal,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { minimumRowActions, productRowActions } from '../pricingTableRowActions'
import { useProductReordering } from 'Cube/domain'
import { DraggableAttributes } from '@dnd-kit/core'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { renderPriceSecondaryRow } from './PricingTableSecondaryRow'

const DragHandle = ({
  id,
  attributes,
  listeners,
  style,
  disabled,
  isDragging,
  'data-testid': dataTestId
}: {
  id: string
  attributes: DraggableAttributes
  listeners: SyntheticListenerMap | undefined
  style: React.CSSProperties
  disabled?: boolean
  isDragging?: boolean
  'data-testid'?: string
}) => (
  <Flex
    id={id}
    opacity={isDragging ? '1' : '0'}
    width="20px"
    alignSelf="stretch"
    position="relative"
    left="8px"
    _groupHover={disabled ? {} : { opacity: 1 }}
    alignItems="center"
    justifyContent="flex-end"
    background={GreyWhite}
    cursor={disabled ? 'default' : 'grab'}
    pr="8px"
    {...(disabled ? {} : attributes)}
    {...(disabled ? {} : listeners)}
    style={style}
    zIndex={100}
    data-testid={dataTestId}
  >
    <IconDragHandle color={GreyGrey60} style={{ flexShrink: '0' }} />
  </Flex>
)

export const PricingTableCompactPricingRow = ({
  pricingRow,
  hasMinimum,
  dragDisabled,
  isDragging
}: {
  hasMinimum: boolean
  pricingRow: PricingRow
  /**
   * Hides and disables the drag handle for the row - particularly
   * useful when another row is being dragged. The drag behaviour
   * being available at all is controlled by the re-order functionality
   * being enabled.
   */
  dragDisabled?: boolean
  isDragging?: boolean
}) => {
  const { enabled: reorderEnabled } = useProductReordering()
  const isGlobalDiscount = pricingRow.isGlobalDiscountRow

  const reorderingAvailable = reorderEnabled && !isGlobalDiscount
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: pricingRow.id,
      disabled: !reorderingAvailable
    })
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 9999 : 'auto',
    touchAction: 'none',
    transition
  }

  const secondaryRow =
    pricingRow.priceStructure && pricingRow.priceCurrency
      ? renderPriceSecondaryRow({
          structure: pricingRow.priceStructure,
          currency: pricingRow.priceCurrency
        })
      : null
  const pricingModelLabel = pricingRow.priceStructure
    ? getPricingModelLabel(pricingRow.priceStructure)
    : null
  const hasSecondaryRow = Boolean(secondaryRow)
  return (
    <Flex
      position="relative"
      className="group"
      width="100%"
      alignItems="stretch"
      paddingRight="16px"
    >
      {reorderingAvailable && (
        <DragHandle
          id={`drag-handle-${pricingRow.id}`}
          attributes={attributes}
          listeners={listeners}
          style={dndStyle}
          disabled={dragDisabled}
          isDragging={isDragging}
          data-testid={`cube.priceRow.${pricingRow.id}.dragHandle`}
        />
      )}
      <Grid
        width="100%"
        backgroundColor={GreyWhite}
        data-test-is-highlighted={pricingRow.highlighted}
        data-test-has-minimum={hasMinimum}
        key={pricingRow.id}
        templateColumns={COMPACT_COLUMN_WIDTHS}
        cursor="pointer"
        minHeight="36px"
        _hover={{
          backgroundColor: GreyGrey10
        }}
        data-testid={`cube.priceRow.${pricingRow.id}`}
        position="relative"
        borderRadius="8px"
        _after={{
          content: '""',
          position: 'absolute',
          borderRadius: '4px',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: IndigoIndigo10,
          opacity: pricingRow.highlighted ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
          zIndex: 0
        }}
        ref={setNodeRef}
        onClick={pricingRow.onClick}
        marginLeft={isGlobalDiscount ? '24px' : '4px'}
        style={dndStyle}
      >
        <Flex
          {...STYLES.tableCell}
          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          paddingLeft={hasMinimum ? '12px' : '4px'}
          gridRow={hasSecondaryRow ? '1 / 3' : undefined}
          alignItems={hasSecondaryRow ? 'flex-start' : 'center'}
          paddingTop={hasSecondaryRow ? '8px' : undefined}
        >
          {pricingRow.name}
        </Flex>
        <Flex {...STYLES.tableCell} color={GreyGrey60}>
          {toBillingFrequencyLabel(pricingRow.billingFrequency)}
        </Flex>
        <Flex {...STYLES.tableCell}>
          {pricingRow.discount ? (
            <Flex
              key={pricingRow.discount.id}
              data-testid={`cube.priceRow.${pricingRow.id}.discount`}
              alignContent="center"
              gap="4px"
              _hover={{
                opacity: 0.5
              }}
              onClick={e => {
                e.stopPropagation()
                pricingRow.availableFeatures.canEditDiscount?.handler()
              }}
            >
              {pricingRow.discount.label}
            </Flex>
          ) : null}
        </Flex>
        <Flex {...STYLES.tableCell} justifyContent="flex-end" gap="6px">
          <Tooltip label={pricingModelLabel ?? ''} placement="top">
            <Box>
              <Flex justifyContent="flex-end" gap="6px" alignItems="center">
                {pricingRow.price !== pricingRow.preDiscountPrice && (
                  <Box
                    textDecoration="line-through"
                    textAlign="right"
                    color={GreyGrey60}
                    data-testid={`cube.priceRow.${pricingRow.id}.preDiscountPrice`}
                  >
                    {pricingRow.preDiscountPrice}
                  </Box>
                )}
                <Box
                  textAlign="right"
                  data-testid={`cube.priceRow.${pricingRow.id}.price`}
                >
                  {pricingRow.price}
                </Box>
              </Flex>
            </Box>
          </Tooltip>
        </Flex>
        <Flex
          {...STYLES.tableCell}
          maxWidth="36px"
          height="100%"
          padding="0px"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <SequenceMenu
            alignment="bottom-right"
            data-testid={`cube.priceRow.${pricingRow.id}.menu`}
            menuButton={
              <Button variant="ghost" size="24px">
                <Center flexShrink="0">
                  <IconEllipsisHorizontal color={GreyGrey60} />
                </Center>
              </Button>
            }
            items={productRowActions(pricingRow)}
          />
        </Flex>
        {secondaryRow && (
          <Box
            {...Primary12Regular}
            gridColumn="2 / -2"
            paddingLeft="8px"
            paddingRight="16px"
            paddingBottom="8px"
            position="relative"
            zIndex={1}
            data-testid={`cube.priceRow.${pricingRow.id}.secondaryRow`}
          >
            {secondaryRow}
          </Box>
        )}
      </Grid>
    </Flex>
  )
}

export const PricingTableCompactMinimumRow = ({
  minimumRow
}: {
  minimumRow: MinimumRow
}) => {
  const { enabled: reorderEnabled } = useProductReordering()
  return (
    <Flex
      position="relative"
      className="group"
      width="100%"
      alignItems="center"
      paddingRight="16px"
      paddingLeft={reorderEnabled ? '20px' : '0px'}
      backgroundColor={GreyWhite}
    >
      <Grid
        width="100%"
        backgroundColor="transparent"
        data-testid={`cube.minimumRow.${minimumRow.id}`}
        data-test-is-highlighted={minimumRow.highlighted}
        templateColumns={COMPACT_COLUMN_WIDTHS}
        alignItems="center"
        key={`group${minimumRow.id}`}
        _hover={{
          transition: 'none',
          backgroundColor: GreyGrey10
        }}
        onClick={minimumRow.actions.edit.handler}
        cursor="pointer"
        position="relative"
        borderRadius="8px"
        _after={{
          content: '""',
          position: 'absolute',
          borderRadius: '8px',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: IndigoIndigo10,
          opacity: minimumRow.highlighted ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
          zIndex: 0
        }}
      >
        <Flex
          {...STYLES.tableCell}
          style={{ textWrap: 'wrap' }}
          {...Primary14SemiBold}
        >
          {minimumRow.name}
        </Flex>
        <Flex {...STYLES.tableCell} color={GreyGrey60}>
          {toBillingFrequencyLabel(minimumRow.billingFrequency)}
        </Flex>
        <Flex {...STYLES.tableCell}></Flex>
        <Flex {...STYLES.tableCell} justifyContent="flex-end">
          {minimumRow.value}
        </Flex>
        <Flex
          {...STYLES.tableCell}
          maxWidth="36px"
          height="36px"
          padding="0px"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <SequenceMenu
            alignment="bottom-right"
            data-testid={`cube.minimumRow.${minimumRow.id}.menu`}
            menuButton={
              <Button variant="ghost" size="24px">
                <Center flexShrink="0">
                  <IconEllipsisHorizontal color={GreyGrey60} />
                </Center>
              </Button>
            }
            items={minimumRowActions(minimumRow)}
          />
        </Flex>
      </Grid>
    </Flex>
  )
}
